import React from "react";
import { ArrowRight, Menu, X, User } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { SOCIAL_ICONS } from "@/components/site/socialIcons";
import { LangSwitch, useScrolled } from "../shared/TemplateShell";
import { NavMore, useInlineNavSplit } from "../shared/InlineNav";
import { useSiteContent } from "../shared/useSiteData";

/**
 * Minimal / Clean — the theme's CHROME (header + footer), in one place.
 *
 * Extracted from MinimalTemplate so that the homepage and the interior pages
 * (templates/ThemedPageLayout.jsx) render the SAME two components instead of the homepage
 * keeping an inline header and the interior pages falling back to Classic's (BUG-006 /
 * BUG-032). The template still owns everything between them.
 *
 * Idiom: one floating white pill, centred, hairline shadow on scroll; black pill CTA; the
 * mobile panel is the same pill geometry dropped below it as a rounded card.
 */

/** Shared by the homepage `<main>` and by ThemedPageLayout, so the ground is identical. */
export const MINIMAL_MAIN_CLASS = "bg-[var(--tpl-bg)] text-[var(--tpl-text)] min-h-screen";

const LINK = "text-sm font-medium text-black/60 hover:text-black transition-colors whitespace-nowrap";

/**
 * The FR/EN/ES switch: the selected language is a FILLED chip, never a colour change alone.
 *
 * This theme already did the right thing — a black chip on its white pill — and it is stated here
 * as a named pair because the four other themes did not: two of them coloured the active language
 * the same as their own header ground (white type on Organic's near-white ground, black type on
 * Bold's black bar), so the language the visitor was reading in was the one they could not see.
 * A chip brings its own ground, which is what makes the rule hold on any theme.
 */
const LANG_ACTIVE = "bg-black text-white";
const LANG_IDLE = "text-black/40 hover:text-black";

/**
 * How many of the ten nav links this theme can draw inline (BUG-045 — see
 * shared/InlineNav.jsx for why a fixed count and not a breakpoint).
 *
 * This theme's header is a CENTRED PILL with no `max-w`, so its width is its content's: when
 * the content outgrows the viewport the pill does not overflow, it WRAPS to two lines and the
 * CTA leaves the screen. Measured with all ten links, in French, on a 24-character practice
 * name: the pill grew from 56px to 72px tall at 1280-1440px and the CTA was clipped 22px past
 * the right edge at 1280px. Labels are 14px with `gap-5` (20px), ~95px each; the desktop nav
 * only exists at `xl` and above, so only the `xl` number is ever on screen here — 6 sheds
 * four links' worth of width, far more than the ~80px the worst case needs.
 */
const INLINE_CAPACITY = { lg: 3, xl: 6 };

export const MinimalHeader = ({ toggles = {} }) => {
  const c = useSiteContent(toggles);
  const tt = useT("templates");
  const nav = c.navModel;
  // Sections are same-page anchors, pages are interior routes; the theme draws them as one
  // row because to a visitor they are one menu.
  const links = [...nav.sections, ...nav.pages];
  const { inline, overflow } = useInlineNavSplit(nav, INLINE_CAPACITY);
  const [open, setOpen] = React.useState(false);
  const scrolled = useScrolled();

  return (
    <>
      <header className="fixed top-5 inset-x-0 z-50 flex justify-center px-4" data-testid="site-navbar">
        <div className={`flex items-center gap-5 rounded-full pl-6 pr-2 py-2 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl shadow-[0_10px_40px_-20px_rgba(0,0,0,0.3)] border border-black/5" : "bg-white/70 backdrop-blur"}`}>
          <a href={nav.home} className="tpl-head font-bold text-lg tracking-tight pr-1" data-testid="logo-home">{nav.brand}</a>
          <nav className="hidden xl:flex items-center gap-5">
            {inline.map((l) => (
              <a key={l.key} href={l.href} data-testid={`nav-link-${l.key}`} className={LINK}>{l.label}</a>
            ))}
            <NavMore
              items={overflow}
              className={LINK}
              menuClassName="bg-white rounded-2xl shadow-xl border border-black/5"
              itemClassName={LINK}
            />
          </nav>
          <LangSwitch lang={c.lang} setLang={c.setLang} className="hidden sm:flex" activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
          {nav.account && (
            <a href={nav.account.href} data-testid="nav-client-portal" aria-label={nav.account.label} title={nav.account.label} className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full border border-black/10 text-black/60 hover:text-black hover:border-black/40 transition-colors">
              <User size={16} />
            </a>
          )}
          {nav.book && (
            <a href={nav.book.href} data-testid="nav-book-cta" className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-black text-white text-sm font-medium px-5 py-2.5 hover:-translate-y-0.5 transition-transform whitespace-nowrap">
              {nav.book.label} <ArrowRight size={15} />
            </a>
          )}
          <button
            className="xl:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? tt("shared.nav.closeMenu") : tt("shared.nav.openMenu")}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {open && (
        <div className="fixed top-24 inset-x-4 z-40 bg-white rounded-2xl shadow-xl border border-black/5 p-6 xl:hidden" data-testid="mobile-menu">
          <div className="flex flex-col divide-y divide-black/5">
            {links.map((l) => (
              <a key={l.key} href={l.href} onClick={() => setOpen(false)} className="text-base font-medium py-3">{l.label}</a>
            ))}
            {nav.account && (
              <a href={nav.account.href} onClick={() => setOpen(false)} className="inline-flex items-center gap-2 text-base font-medium py-3">
                <User size={16} /> {nav.account.label}
              </a>
            )}
          </div>
          <div className="mt-5 flex items-center justify-between gap-4">
            <LangSwitch lang={c.lang} setLang={c.setLang} activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
            {nav.book && (
              <a href={nav.book.href} onClick={() => setOpen(false)} className="rounded-full bg-black text-white text-sm font-medium px-5 py-3">{nav.book.label}</a>
            )}
          </div>
        </div>
      )}
    </>
  );
};

/**
 * The oversized mailto stays the theme's signature, but it sits above a real sitemap
 * (BUG-026): the link columns come from the shared model.
 */
export const MinimalFooter = ({ toggles = {} }) => {
  const f = useSiteContent(toggles).footerModel;

  return (
    <footer className="border-t border-black/10 px-6 py-16" data-testid="site-footer">
      <div className="max-w-6xl mx-auto">
        <a href={`mailto:${f.contact.email}`} className="tpl-head block text-4xl md:text-6xl font-bold tracking-tight hover:text-black/60 transition-colors break-all">{f.contact.email}</a>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 border-t border-black/10 pt-10">
          <div>
            <div className="tpl-head text-lg font-bold">{f.brand}</div>
            {/* BUG-210: no dictionary sample any more — an unset tagline is a fact the
                practice hasn't stated, not filler text. */}
            {f.tagline && <p className="mt-2 text-sm text-black/45 leading-relaxed">{f.tagline}</p>}
          </div>
          {f.sections.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-black/35">{f.headings.navigate}</div>
              <div className="mt-4 space-y-2 text-sm">
                {f.sections.map((s) => (
                  <a key={s.key} href={s.href} data-testid={`footer-section-${s.key}`} className="block text-black/60 hover:text-black transition-colors">{s.label}</a>
                ))}
              </div>
            </div>
          )}
          {f.pages.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-black/35">{f.headings.explore}</div>
              <div className="mt-4 space-y-2 text-sm">
                {f.pages.map((p) => (
                  <a key={p.key} href={p.href} data-testid={`footer-page-${p.key}`} className="block text-black/60 hover:text-black transition-colors">{p.label}</a>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-black/35">{f.headings.contact}</div>
            <div className="mt-4 space-y-2 text-sm text-black/60">
              <div className="break-all">{f.contact.email}</div>
              <div>{f.contact.place}</div>
            </div>
            {f.socials.length > 0 && (
              <div className="mt-5 flex items-center gap-2.5">
                {f.socials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.key];
                  return (
                    <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`footer-social-${s.key}`} className="w-9 h-9 border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                      <Icon size={15} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-black/10 text-sm text-black/40" data-testid="footer-copyright">
          © {f.year} {f.brand} — {f.rights}
        </div>
      </div>
    </footer>
  );
};

/** The chrome contract templates/registry.js registers for t2_minimal. */
export const MINIMAL_CHROME = {
  Header: MinimalHeader,
  Footer: MinimalFooter,
  mainClass: MINIMAL_MAIN_CLASS,
};

export default MINIMAL_CHROME;
